import React, { useEffect, useState, useCallback } from 'react';
import { Trash2, Eye, RefreshCw, X, Globe } from 'lucide-react';
import { api, formatApiError } from '../../lib/api';
import { PageHeader, AdminCard, Loading, Empty, StatusBadge, Btn, Select, formatDate } from './shared';
import { useToast } from '../../hooks/use-toast';

const STATUSES = ['all', 'new', 'contacted', 'approved', 'closed'];

export default function AdminPartners() {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('all');
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = status === 'all' ? {} : { status };
      const res = await api.get('/api/admin/partners', { params });
      setItems(res.data.items || []);
    } catch (err) {
      toast({ title: 'Failed to load', description: formatApiError(err) });
    } finally {
      setLoading(false);
    }
  }, [status, toast]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, s) => {
    try {
      await api.patch(`/api/admin/partners/${id}`, { status: s });
      toast({ title: 'Status updated' });
      setSelected((p) => p && p.id === id ? { ...p, status: s } : p);
      load();
    } catch (err) { toast({ title: 'Failed', description: formatApiError(err) }); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this application?')) return;
    try {
      await api.delete(`/api/admin/partners/${id}`);
      toast({ title: 'Deleted' });
      setSelected(null); load();
    } catch (err) { toast({ title: 'Delete failed', description: formatApiError(err) }); }
  };

  return (
    <>
      <PageHeader
        title="Partner Applications"
        subtitle="White Label reseller programme submissions"
        action={<Btn variant="ghost" onClick={load}><RefreshCw size={14} /> Refresh</Btn>}
      />

      <AdminCard>
        <div className="mb-4">
          <Select value={status} onChange={(e) => setStatus(e.target.value)} className="md:w-52">
            {STATUSES.map((s) => <option key={s} value={s}>{s.toUpperCase()}</option>)}
          </Select>
        </div>

        {loading ? <Loading /> : items.length === 0 ? <Empty>No partner applications yet.</Empty> : (
          <div className="overflow-x-auto -mx-5 md:-mx-6 px-5 md:px-6">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-slate-400 uppercase tracking-wider">
                  <th className="py-2">Company</th>
                  <th className="py-2">Contact</th>
                  <th className="py-2 hidden md:table-cell">Country</th>
                  <th className="py-2 hidden lg:table-cell">Date</th>
                  <th className="py-2">Status</th>
                  <th className="py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {items.map((p) => (
                  <tr key={p.id} className="hover:bg-white/5" data-testid={`partner-row-${p.id}`}>
                    <td className="py-3 font-semibold text-white">{p.companyName}</td>
                    <td className="py-3 text-slate-300">{p.contactName}<br /><span className="text-xs text-slate-500">{p.email}</span></td>
                    <td className="py-3 text-slate-300 hidden md:table-cell">{p.country}</td>
                    <td className="py-3 text-slate-400 hidden lg:table-cell">{formatDate(p.created_at)}</td>
                    <td className="py-3"><StatusBadge status={p.status} /></td>
                    <td className="py-3 text-right">
                      <div className="inline-flex gap-1">
                        <Btn size="sm" variant="ghost" onClick={() => setSelected(p)}><Eye size={12} /> View</Btn>
                        <Btn size="sm" variant="danger" onClick={() => remove(p.id)}><Trash2 size={12} /></Btn>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </AdminCard>

      {selected && (
        <div className="fixed inset-0 z-50 flex" onClick={() => setSelected(null)}>
          <div className="flex-1 bg-black/60 backdrop-blur-sm" />
          <aside className="w-full max-w-xl bg-slate-900 border-l border-white/10 p-6 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-xl font-bold text-white">Application</h3>
              <button onClick={() => setSelected(null)} className="p-2 rounded-lg hover:bg-white/5"><X size={18} /></button>
            </div>

            <section className="grid grid-cols-2 gap-3 text-sm">
              {[
                ['Company', selected.companyName], ['Website', selected.website],
                ['Country', selected.country], ['Company size', selected.companySize],
                ['Years in business', selected.yearsInBusiness],
                ['Contact', selected.contactName], ['Job title', selected.jobTitle],
                ['Email', selected.email], ['Phone', selected.phone],
                ['Business type', selected.businessType], ['Customer base', selected.customerBase],
                ['Revenue target', selected.revenueTarget], ['Timeline', selected.timeline],
              ].map(([k, v]) => v ? (
                <div key={k} className="bg-white/5 border border-white/10 rounded-lg p-3">
                  <p className="text-[10px] uppercase tracking-wider text-slate-400">{k}</p>
                  <p className="text-white mt-0.5 text-sm">{v}</p>
                </div>
              ) : null)}
            </section>

            {(selected.regions?.length || selected.servicesOffered?.length) ? (
              <div className="mt-4 space-y-3">
                {selected.regions?.length ? (
                  <div>
                    <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400 mb-1.5"><Globe size={12} className="inline mr-1" /> Regions</p>
                    <div className="flex flex-wrap gap-1.5">
                      {selected.regions.map((r) => <span key={r} className="text-xs px-2 py-0.5 rounded-full bg-violet-500/20 text-violet-200 border border-violet-500/30">{r}</span>)}
                    </div>
                  </div>
                ) : null}
                {selected.servicesOffered?.length ? (
                  <div>
                    <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400 mb-1.5">Services</p>
                    <div className="flex flex-wrap gap-1.5">
                      {selected.servicesOffered.map((r) => <span key={r} className="text-xs px-2 py-0.5 rounded-full bg-fuchsia-500/20 text-fuchsia-200 border border-fuchsia-500/30">{r}</span>)}
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}

            {selected.whyPartner && (
              <div className="mt-4">
                <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400 mb-1.5">Why partner</p>
                <p className="text-sm text-slate-200 leading-relaxed whitespace-pre-wrap">{selected.whyPartner}</p>
              </div>
            )}
            {selected.whitelabelScope && (
              <div className="mt-4">
                <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400 mb-1.5">White-label scope</p>
                <p className="text-sm text-slate-200 leading-relaxed whitespace-pre-wrap">{selected.whitelabelScope}</p>
              </div>
            )}

            <div className="mt-6 pt-4 border-t border-white/10">
              <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400 mb-2">Status</p>
              <div className="flex flex-wrap gap-2">
                {['new', 'contacted', 'approved', 'closed'].map((s) => (
                  <button
                    key={s}
                    onClick={() => updateStatus(selected.id, s)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold uppercase border ${
                      selected.status === s
                        ? 'bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white border-transparent'
                        : 'bg-white/5 text-slate-300 border-white/10 hover:bg-white/10'
                    }`}
                  >{s}</button>
                ))}
              </div>
              <div className="mt-4 flex gap-2">
                <Btn variant="danger" onClick={() => remove(selected.id)}><Trash2 size={14} /> Delete</Btn>
              </div>
            </div>
          </aside>
        </div>
      )}
    </>
  );
}
